import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { UsersIcon, ChevronLeftIcon, ChevronRightIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
import { useRecipients, useRecipientActivity } from '../hooks/useComms';
import { Bar } from './CommsDispatch';

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const num = (v) => Number(v || 0);
const pct = (a, b) => (b ? Math.round((num(a) / num(b)) * 100) : 0);

// Responsiveness: how long after a message was sent this person read /
// acknowledged it. Derived from the timestamps the endpoint already returns.
const durMs = (from, to) => (from && to ? new Date(to) - new Date(from) : null);
function fmtDuration(ms) {
  if (ms == null || ms < 0) return '—';
  const mins = Math.round(ms / 60000);
  if (mins < 1) return '<1m';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}
const avg = (arr) => (arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : null);

function Tile({ label, value, sub, tone }) {
  const toneCls = tone === 'amber' ? 'text-amber-600' : tone === 'ok' ? 'text-emerald-600' : 'text-gray-900';
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center">
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-2xl font-bold tabular-nums ${toneCls}`}>{value}</div>
      {sub != null && <div className="text-xs text-gray-500">{sub}</div>}
    </div>
  );
}

// One person's full message history + read/acknowledge engagement.
function RecipientDetail({ entraId, onBack }) {
  const { data, isLoading, isError } = useRecipientActivity(entraId);
  if (isLoading) return <div className="p-6"><p className="text-sm text-gray-400">Loading…</p></div>;
  if (isError || !data) {
    return (
      <div className="p-6">
        <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark"><ChevronLeftIcon className="h-4 w-4" /> Back to recipients</button>
        <p className="text-sm text-gray-400">No message activity for this recipient.</p>
      </div>
    );
  }
  const received = num(data.received);
  const read = num(data.read_count);
  const ackReq = num(data.ack_required);
  const ack = num(data.ack_count);
  const outstanding = num(data.outstanding);
  const msgs = data.messages || [];
  const avgRead = avg(msgs.map((m) => durMs(m.created_at, m.read_at)).filter((v) => v != null && v >= 0));
  const avgAck = avg(msgs.filter((m) => m.requires_ack).map((m) => durMs(m.created_at, m.acknowledged_at)).filter((v) => v != null && v >= 0));
  return (
    <div className="p-6">
      <button type="button" onClick={onBack} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark"><ChevronLeftIcon className="h-4 w-4" /> Back to recipients</button>
      <h1 className="mb-0.5 text-2xl font-bold text-gray-900">{data.display_name || 'Recipient'}</h1>
      <p className="mb-5 text-sm text-gray-500">{data.email || entraId}</p>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Tile label="Received" value={received} />
        <Tile label="Read" value={`${read}/${received}`} sub={`${pct(read, received)}%`} />
        <Tile label="Acknowledged" value={ackReq ? `${ack}/${ackReq}` : '—'} sub={ackReq ? `${pct(ack, ackReq)}%` : 'none required'} />
        <Tile label="Avg time to read" value={avgRead != null ? fmtDuration(avgRead) : '—'} sub={avgRead != null ? 'after sending' : 'no reads yet'} />
        <Tile label="Avg time to ack" value={avgAck != null ? fmtDuration(avgAck) : '—'} sub={avgAck != null ? 'after sending' : (ackReq ? 'none yet' : 'none required')} />
        <Tile label="Outstanding acks" value={outstanding} tone={outstanding > 0 ? 'amber' : 'ok'} />
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <th className="px-4 py-2.5">Message</th>
              <th className="px-4 py-2.5">Sent</th>
              <th className="px-4 py-2.5">Read</th>
              <th className="px-4 py-2.5">Acknowledged</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {(data.messages || []).map((m) => (
              <tr key={m.comm_id} className="align-top">
                <td className="px-4 py-2.5 text-sm font-medium text-gray-800">
                  {m.priority === 'high' && <span className="mr-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-600">High</span>}
                  {m.subject}
                  {!!m.requires_ack && <CheckBadgeIcon className="ml-1 inline h-4 w-4 text-emerald-500" title="Requires acknowledgement" />}
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-500">{fmtDate(m.created_at)}</td>
                <td className="px-4 py-2.5 text-xs">
                  {m.read_at ? (
                    <span>
                      <span className="text-gray-600">{fmtDate(m.read_at)}</span>
                      <span className="block text-[11px] text-gray-400">{fmtDuration(durMs(m.created_at, m.read_at))} after sending</span>
                    </span>
                  ) : <span className="font-medium text-amber-600">Unread</span>}
                </td>
                <td className="px-4 py-2.5 text-xs">
                  {!m.requires_ack ? <span className="text-gray-300">—</span>
                    : m.acknowledged_at ? (
                      <span>
                        <span className="inline-flex items-center gap-1 font-medium text-emerald-600"><CheckBadgeIcon className="h-4 w-4" />{fmtDate(m.acknowledged_at)}</span>
                        <span className="block text-[11px] text-gray-400">{fmtDuration(durMs(m.created_at, m.acknowledged_at))} after sending</span>
                      </span>
                    ) : <span className="font-medium text-amber-600">Awaiting{num(m.reminders_sent) > 0 ? ` · ${num(m.reminders_sent)} reminder${num(m.reminders_sent) === 1 ? '' : 's'}` : ''}</span>}
                </td>
              </tr>
            ))}
            {(!data.messages || data.messages.length === 0) && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400">No messages.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Searchable list of everyone who has received a comm, with their aggregate
// read/acknowledge engagement.
function RecipientList({ onOpen }) {
  const { data: recipients, isLoading } = useRecipients();
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = recipients || [];
    if (!t) return list;
    return list.filter((r) => `${r.display_name || ''} ${r.email || ''}`.toLowerCase().includes(t));
  }, [recipients, q]);

  return (
    <>
      <input
        type="text"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by name or email…"
        className="mb-4 w-full max-w-sm rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
      />
      {isLoading && <p className="text-sm text-gray-400">Loading…</p>}
      {recipients && (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2.5">Person</th>
                <th className="px-4 py-2.5 text-right">Received</th>
                <th className="px-4 py-2.5">Read</th>
                <th className="px-4 py-2.5">Acknowledged</th>
                <th className="px-4 py-2.5 text-right">Outstanding</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filtered.map((r) => (
                <tr key={r.entra_object_id} className="cursor-pointer hover:bg-brand-light/40" onClick={() => onOpen(r.entra_object_id)}>
                  <td className="px-4 py-2.5">
                    <div className="text-sm font-medium text-gray-800">{r.display_name || '—'}</div>
                    <div className="text-xs text-gray-400">{r.email}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-sm text-gray-600 tabular-nums">{num(r.received)}</td>
                  <td className="px-4 py-2.5"><Bar value={num(r.read_count)} total={num(r.received)} /></td>
                  <td className="px-4 py-2.5">{num(r.ack_required) > 0 ? <Bar value={num(r.ack_count)} total={num(r.ack_required)} tone="green" /> : <span className="text-xs text-gray-300">—</span>}</td>
                  <td className="px-4 py-2.5 text-right text-sm tabular-nums">{num(r.outstanding) > 0 ? <span className="font-semibold text-amber-600">{num(r.outstanding)}</span> : <span className="text-gray-300">0</span>}</td>
                  <td className="px-4 py-2.5 text-right"><ChevronRightIcon className="h-4 w-4 text-gray-300" /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-400">{q ? 'No matches.' : 'No recipients yet.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// Message trends per user: pick a person from the list, drill into their
// full read/acknowledge history. Sibling to Message Analytics (which is
// per-message); this is per-recipient. Route: /comms/recipients[/:entraId].
function RecipientActivity() {
  const { entraId } = useParams();
  const navigate = useNavigate();

  if (entraId) return <RecipientDetail entraId={entraId} onBack={() => navigate('/comms/recipients')} />;

  return (
    <div className="p-6">
      <button type="button" onClick={() => navigate('/?dept=communications')} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Communications
      </button>
      <h1 className="mb-1 flex items-center gap-2 text-2xl font-bold text-gray-900">
        <UsersIcon className="h-7 w-7 text-brand" /> Recipient Activity
      </h1>
      <p className="mb-6 text-sm text-gray-500">How each person engages with communications. Select someone to see their full history of reads and acknowledgements.</p>
      <RecipientList onOpen={(id) => navigate(`/comms/recipients/${id}`)} />
    </div>
  );
}

export default RecipientActivity;
